import {useMemo} from 'react';
import moment from 'moment';
import {useAppSelector} from './reduxHooks';
import {selectPrimaryCampaign} from '../slices/storesSlice';
import {useGetStoreDetailsQuery} from '../api/storesApi';
import {useStoreSlug} from './useStoreSlug';
import {getCoreDateFormat} from '../utils/helpers';

interface UseGroupOrderTimeline {
  autoCancelDate: moment.Moment;
  maxCloseDate: moment.Moment;
  tomorrowDate: moment.Moment;
  autoCancelDateFormatted: string;
  maxCloseDateFormatted: string;
  tomorrowDateFormatted: string;
  closeDateFormatted: string;
  daysUntilClose: string;
  daysUntilAutoCancel: number;
  closingSoon: boolean;
  closeDate: moment.Moment;
}

export const useGroupOrderTimeline = (): UseGroupOrderTimeline => {
  const urlSlug = useStoreSlug();
  useGetStoreDetailsQuery(urlSlug);
  const primaryCampaign = useAppSelector(selectPrimaryCampaign);
  const closeDate = moment(primaryCampaign?.closesAt).endOf('day');
  const currentDate = moment().startOf('day');
  const closingSoon = primaryCampaign?.closingSoon;
  const daysUntilClose = primaryCampaign?.timeRemainingInWords;

  const autoCancelDate = useMemo(() => {
    return closeDate.clone().add(2, 'weeks');
  }, [closeDate]);

  const autoCancelDateFormatted = useMemo(() => {
    return getCoreDateFormat(autoCancelDate);
  }, [autoCancelDate]);

  const closeDateFormatted = useMemo(() => {
    return closeDate.format('dddd, MMMM D, YYYY');
  }, [closeDate]);

  const maxCloseDate = useMemo(() => {
    return moment(primaryCampaign?.createdAt).endOf('day').add(89, 'days');
  }, [primaryCampaign?.createdAt]);

  const maxCloseDateFormatted = useMemo(() => {
    return getCoreDateFormat(maxCloseDate);
  }, [maxCloseDate]);

  const tomorrowDate = useMemo(() => {
    return moment().add(1, 'day').startOf('day');
  }, []);

  const tomorrowDateFormatted = useMemo(() => {
    return getCoreDateFormat(tomorrowDate);
  }, [tomorrowDate]);

  const daysUntilAutoCancel = useMemo(() => {
    return Math.round(autoCancelDate.diff(currentDate, 'days', true));
  }, [autoCancelDate]);

  return {
    autoCancelDate,
    maxCloseDate,
    tomorrowDate,
    autoCancelDateFormatted,
    maxCloseDateFormatted,
    tomorrowDateFormatted,
    closeDateFormatted,
    daysUntilClose,
    daysUntilAutoCancel,
    closingSoon,
    closeDate
  };
};

export default useGroupOrderTimeline;
